import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../components/Header";
import Footer from "../components/Footer";
import "../styles/Search.css";
import API_BASE_URL from "../config";
import { getPracticeCategories } from "../constants/practiceAreas";
import { FiUser, FiCalendar, FiMapPin, FiSearch, FiXCircle } from "react-icons/fi";
import { FaCrown, FaMedal, FaAward } from "react-icons/fa";
import { HiSparkles } from "react-icons/hi";
import { BsBarChartFill } from "react-icons/bs";

/* ======================
   GEOAPIFY CONFIG
====================== */
const GEOAPIFY_API_KEY = "bf58eac2ef4d43d3b7c5661f657f2a03";

export default function Search() {
    /* ======================
       STATE
    ====================== */
    const [practiceCategories, setPracticeCategories] = useState({});
    const [randomSolicitors, setRandomSolicitors] = useState([]);
    const [solicitors, setSolicitors] = useState([]);
    const [filteredSolicitors, setFilteredSolicitors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [hasSearched, setHasSearched] = useState(false); // ⭐ KEY
    const [ratingsMap, setRatingsMap] = useState({});

    /* ======================
       LOCATION AUTOCOMPLETE
    ====================== */
    const [cityQuery, setCityQuery] = useState("");
    const [citySuggestions, setCitySuggestions] = useState([]);
    // eslint-disable-next-line no-unused-vars
    const [fetchingCities, setFetchingCities] = useState(false);


    /* ======================
       PRACTICE FILTERS
    ====================== */
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedArea, setSelectedArea] = useState("");

    /* ======================
       NEW FILTERS (FROM IMAGE)
    ====================== */
    const [selectedExperience, setSelectedExperience] = useState("");
    const [selectedGender, setSelectedGender] = useState("");
    // eslint-disable-next-line no-unused-vars
    const [selectedRating, setSelectedRating] = useState("");
    const [selectedAdvocateLevel, setSelectedAdvocateLevel] = useState("");
    const [selectedSortBy, setSelectedSortBy] = useState("");

    const navigate = useNavigate();


    /* ======================
       FILTER OPTIONS
    ====================== */
    const experienceOptions = [
        { value: "", label: "Select Experience" },
        { value: "0-2", label: "0-2 years" },
        { value: "2-5", label: "2-5 years" },
        { value: "5-10", label: "5-10 years" },
        { value: "10+", label: "10+ years" }
    ];

    const genderOptions = [
        { value: "", label: "Select Gender" },
        { value: "MALE", label: "Male" },
        { value: "FEMALE", label: "Female" },
        { value: "OTHER", label: "Other" }
    ];

    // eslint-disable-next-line no-unused-vars
    const ratingOptions = [
        { value: "", label: "Select Ratings" },
        { value: "5", label: "★★★★★ (5)" },
        { value: "4", label: "★★★★☆ (4+)" },
        { value: "3", label: "★★★☆☆ (3+)" },
        { value: "2", label: "★★☆☆☆ (2+)" }
    ];

    const advocateLevelOptions = [
        { value: "", label: "Select Level" },
        { value: "JUNIOR", label: "Junior" },
        { value: "MID", label: "Mid-Level" },
        { value: "SENIOR", label: "Senior" }
    ];

    const sortByOptions = [
        { value: "", label: "Sort By" },
        { value: "badge", label: "By Badge" },
        { value: "experience", label: "By Experience" },
        { value: "alphabetical", label: "By Alphabetically" },
        { value: "rating", label: "By Rating" }
    ];


    const location = useLocation();

    useEffect(() => {
        const loadPracticeAreas = async () => {
            const data = await getPracticeCategories();
            setPracticeCategories(data);
        };

        loadPracticeAreas();
    }, []);

    const loadRatingsForSolicitors = async (list) => {
        try {
            const results = await Promise.all(
                list.map(async (s) => {
                    const res = await fetch(
                        `${API_BASE_URL}/api/reviews/${s._id}`
                    );

                    const reviews = await res.json();

                    if (!Array.isArray(reviews) || reviews.length === 0) {
                        return { id: s._id, avg: 0 };
                    }

                    const avg =
                        reviews.reduce((sum, r) => sum + Number(r.rating || 0), 0) /
                        reviews.length;

                    return { id: s._id, avg };
                })
            );

            const map = {};
            results.forEach(r => {
                map[r.id] = r.avg;
            });

            setRatingsMap(map);
        } catch (err) {
            console.error("Failed to load ratings", err);
        }
    };


    useEffect(() => {
        fetchRandomSolicitors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchRandomSolicitors = async () => {
        try {
            setLoading(true);

            const res = await fetch(`${API_BASE_URL}/api/advocates/random`);
            const data = await res.json();

            const list = Array.isArray(data) ? data : [];

            // setRandomSolicitors(list);
            // setSolicitors(list);
            // loadRatingsForSolicitors(list);
            // setFilteredSolicitors(list);
            const viewed =
                JSON.parse(sessionStorage.getItem("viewedSolicitors")) || [];

            let updatedList = [...list];

            if (viewed.length > 0) {
                updatedList.sort((a, b) => {
                    const aIndex = viewed.indexOf(a._id);
                    const bIndex = viewed.indexOf(b._id);

                    if (aIndex === -1 && bIndex === -1) return 0;
                    if (aIndex === -1) return 1;
                    if (bIndex === -1) return -1;

                    return aIndex - bIndex;
                });
            }

            setRandomSolicitors(updatedList);
            setSolicitors(updatedList);
            loadRatingsForSolicitors(updatedList);
            setFilteredSolicitors(updatedList);

        } catch (err) {
            setError("Failed to load solicitors");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const category = params.get("category");
        const area = params.get("area");

        if (category && area) {
            fetchSolicitorsFromHeader(category, area);
        }
    }, [location.search]);

    const fetchSolicitorsFromHeader = async (category, area) => {
        setLoading(true);
        setError("");

        try {
            const params = new URLSearchParams({ category });
            if (area) params.append("area", area);

            const res = await fetch(
                `${API_BASE_URL}/api/advocates/header?${params.toString()}`
            );

            const data = await res.json();

            const list = Array.isArray(data) ? data : [];

            setSelectedCategory(category);
            setSelectedArea(area || "");

            // setSolicitors(list);
            // setFilteredSolicitors(list);
            const viewed =
                JSON.parse(sessionStorage.getItem("viewedSolicitors")) || [];

            let updatedList = [...list];

            if (viewed.length > 0) {
                updatedList.sort((a, b) => {
                    const aIndex = viewed.indexOf(a._id);
                    const bIndex = viewed.indexOf(b._id);

                    if (aIndex === -1 && bIndex === -1) return 0;
                    if (aIndex === -1) return 1;
                    if (bIndex === -1) return -1;

                    return aIndex - bIndex;
                });
            }

            setSolicitors(updatedList);
            setFilteredSolicitors(updatedList);

        } catch (err) {
            setSolicitors([]);
            setFilteredSolicitors([]);
            setError("Failed to load solicitors");
        } finally {
            setLoading(false);
        }
    };



    /* ======================
       FETCH CITIES (GEOAPIFY)
    ====================== */
    const fetchCities = async (value) => {
        setCityQuery(value);

        if (value.length < 2) {
            setCitySuggestions([]);
            return;
        }

        setFetchingCities(true);

        try {
            const res = await fetch(
                `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
                    value
                )}&limit=6&apiKey=${GEOAPIFY_API_KEY}`
            );

            const data = await res.json();

            const locations =
                data.features?.map((item) => ({
                    id: item.properties.place_id,
                    city: item.properties.city || item.properties.name || "",
                    postcode: item.properties.postcode || "",
                    country: item.properties.country || "",
                    label: [
                        item.properties.city || item.properties.name,
                        item.properties.postcode,
                        item.properties.country
                    ]
                        .filter(Boolean)
                        .join(", ")
                })) || [];

            setCitySuggestions(locations);
        } catch (err) {
            console.error("Location fetch error (Geoapify)", err);
        } finally {
            setFetchingCities(false);
        }
    };


    /* ======================
       FETCH SOLICITORS
    ====================== */
    const fetchSolicitorsByLocation = async () => {

        // ✅ Category & Area are mandatory
        if (!selectedCategory || !selectedArea) {
            toast.error("Please select both practice category and area");
            return;
        }

        setHasSearched(true);
        setLoading(true);
        setError("");

        try {

            // ✅ Build params dynamically
            const params = new URLSearchParams({
                category: selectedCategory,
                area: selectedArea
            });

            // ✅ Add location only if provided
            if (cityQuery.trim()) {
                params.append("location", cityQuery.trim());
            }

            const res = await fetch(
                `${API_BASE_URL}/api/advocates?${params.toString()}`
            );

            const data = await res.json();

            const viewed =
                JSON.parse(sessionStorage.getItem("viewedSolicitors")) || [];

            let updatedList = Array.isArray(data) ? [...data] : [];

            // ✅ MRU ordering
            if (viewed.length > 0) {
                updatedList.sort((a, b) => {
                    const aIndex = viewed.indexOf(a._id);
                    const bIndex = viewed.indexOf(b._id);

                    if (aIndex === -1 && bIndex === -1) return 0;
                    if (aIndex === -1) return 1;
                    if (bIndex === -1) return -1;

                    return aIndex - bIndex;
                });
            }

            setSolicitors(updatedList);
            setFilteredSolicitors(updatedList);
            loadRatingsForSolicitors(updatedList);

            if (updatedList.length === 0) {
                toast.info("No solicitors found for this search");
            }

        } catch (err) {
            console.error("Search error", err);
            setSolicitors([]);
            setFilteredSolicitors([]);
            toast.error("Failed to fetch solicitors. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    /* ======================
       APPLY FILTERS
    ====================== */
    useEffect(() => {
        let result = solicitors.slice();

        if (selectedExperience) {
            result = result.filter(s => {
                const y = Number(s.profile?.experience || 0);
                if (selectedExperience === "0-2") return y < 2;
                if (selectedExperience === "2-5") return y >= 2 && y < 5;
                if (selectedExperience === "5-10") return y >= 5 && y < 10;
                if (selectedExperience === "10+") return y >= 10;
                return true;
            });
        }

        if (selectedGender) {
            result = result.filter(
                s => s.profile?.gender?.toUpperCase() === selectedGender
            );
        }

        if (selectedAdvocateLevel) {
            result = result.filter(
                s => s.profile?.advocateLevel === selectedAdvocateLevel
            );
        }

        if (selectedSortBy === "alphabetical") {
            result = result.slice().sort((a, b) => a.name.localeCompare(b.name));
        }

        if (selectedSortBy === "experience") {
            result = result.slice().sort(
                (a, b) =>
                    Number(b.profile?.experience || 0) -
                    Number(a.profile?.experience || 0)
            );
        }

        if (selectedSortBy === "badge") {
            result = result.slice().sort(
                (a, b) => getBadgeRank(b) - getBadgeRank(a)
            );
        }

        setFilteredSolicitors(result);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedExperience,
        selectedGender,
        selectedAdvocateLevel,
        selectedSortBy,
        solicitors
    ]);



    /* ======================
       CLEAR FILTERS
    ====================== */
    const clearFilter = () => {
        setCityQuery("");
        setCitySuggestions([]);
        setSelectedCategory("");
        setSelectedArea("");
        setSelectedExperience("");
        setSelectedGender("");
        setSelectedRating("");
        setSelectedAdvocateLevel("");
        setSelectedSortBy("");
        setSolicitors([]);
        setFilteredSolicitors([]);
        setError("");
        setSolicitors(randomSolicitors);
        setFilteredSolicitors(randomSolicitors);
        sessionStorage.clear();
    };

    /* ======================
       RESET FILTERS
    ====================== */
    const resetFilters = () => {
        setSelectedExperience("");
        setSelectedGender("");
        setSelectedRating("");
        setSelectedAdvocateLevel("");
        setSelectedSortBy("");
        setFilteredSolicitors(solicitors);
        sessionStorage.removeItem("filteredSolicitors");
    };

    const getExpertBadge = (s) => {
        const level = s.profile?.advocateLevel;
        const totalCases = Number(s.profile?.totalCases || 0);

        if (level === "SENIOR" && totalCases >= 15) {
            return { type: "gold", label: "Expert" };
        }

        if ((level === "MID" && totalCases >= 8) || level === "SENIOR") {
            return { type: "silver", label: "Pro" };
        }

        if (
            (level === "JUNIOR" || level === "MID" || !level) &&
            totalCases < 8
        ) {
            return { type: "bronze", label: "Beginner" };
        }

        return null;
    };

    const getBadgeRank = (s) => {
        const badge = getExpertBadge(s);

        if (!badge) return 0;

        if (badge.type === "gold") return 3;
        if (badge.type === "silver") return 2;
        if (badge.type === "bronze") return 1;

        return 0;
    };



    const renderStars = (rating = 0) => {
        const rounded = Math.round(rating);

        return (
            <div className="stars-wrap" title={rating.toFixed(1)}>
                {[1, 2, 3, 4, 5].map(i => (
                    <span
                        key={i}
                        className={`star ${i <= rounded ? "filled" : ""}`}
                    >
                        ★
                    </span>
                ))}
            </div>
        );
    };


    return (
        <>
            <Header />

            <main className="professional-directory">

                {/* SEARCH SECTION */}
                <section className="filters-section">
                    <div className="container">
                        <div className="filters-container filter-row">

                            {/* CITY */}
                            <div className="filter-item autocomplete-wrapper">
                                <FiMapPin className="input-icon" />
                                <input
                                    type="text"
                                    placeholder="City / Postcode"
                                    value={cityQuery}
                                    onChange={(e) => fetchCities(e.target.value)}
                                    className="search-input"
                                />

                                {citySuggestions.length > 0 && (
                                    <ul className="suggestions-list">
                                        {citySuggestions.map((city) => (
                                            <li
                                                key={city.id}
                                                className="suggestion-item"
                                                onClick={() => {
                                                    setCityQuery(city.postcode || city.city || city.label);
                                                    setCitySuggestions([]);
                                                }}
                                            >
                                                {city.label}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* PRACTICE CATEGORY (REQUIRED) */}
                            <div className="filter-item">
                                <select
                                    className="search-input"
                                    value={selectedCategory}
                                    onChange={(e) => {
                                        setSelectedCategory(e.target.value);
                                        setSelectedArea("");
                                    }}
                                    required
                                >
                                    <option value="">Select Services *</option>

                                    {Object.keys(practiceCategories).map((cat) => (
                                        <option key={cat} value={cat}>
                                            {cat}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-item">
                                <select
                                    className="search-input"
                                    value={selectedArea}
                                    onChange={(e) => setSelectedArea(e.target.value)}
                                    disabled={!selectedCategory}
                                >
                                    <option value="">Practice Area *</option>

                                    {selectedCategory &&
                                        practiceCategories[selectedCategory]?.map((area) => (
                                            <option key={area} value={area}>
                                                {area}
                                            </option>
                                        ))}
                                </select>
                            </div>


                            {/* ACTIONS */}
                            <div className="filter-actions">
                                <button
                                    onClick={fetchSolicitorsByLocation}
                                    className="search-btn"
                                >
                                    <FiSearch style={{ marginRight: "6px" }} />
                                    Search
                                </button>
                                <button
                                    onClick={clearFilter}
                                    className="search-clear-btn"
                                >
                                    <FiXCircle style={{ marginRight: "6px" }} />
                                    Clear
                                </button>
                            </div>

                        </div>

                        {error && <p className="error-text">{error}</p>}
                    </div>
                </section>

                {/* RESULTS SECTION */}
                <section className="results-section">
                    <div className="container results-container">

                        {/* LEFT SIDEBAR FILTERS */}
                        {(hasSearched || randomSolicitors.length > 0) && (
                            <div className="sidebar-filters">
                                <div className="filter-group">
                                    <h3>FILTERS</h3>

                                    <div className="filter-item">
                                        <label>Experience</label>
                                        <select
                                            value={selectedExperience}
                                            onChange={(e) => setSelectedExperience(e.target.value)}
                                            className="filter-select"
                                        >
                                            {experienceOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="filter-item">
                                        <label>Gender</label>
                                        <select
                                            value={selectedGender}
                                            onChange={(e) => setSelectedGender(e.target.value)}
                                            className="filter-select"
                                        >
                                            {genderOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="filter-item">
                                        <label>Advocate Level</label>
                                        <select
                                            value={selectedAdvocateLevel}
                                            onChange={(e) => setSelectedAdvocateLevel(e.target.value)}
                                            className="filter-select"
                                        >
                                            {advocateLevelOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="filter-item">
                                        <label>Sort By</label>
                                        <select
                                            value={selectedSortBy}
                                            onChange={(e) => setSelectedSortBy(e.target.value)}
                                            className="filter-select"
                                        >
                                            {sortByOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <button
                                        onClick={resetFilters}
                                        className="reset-filters-btn"
                                    >
                                        RESET →
                                    </button>
                                </div>
                            </div>
                        )}


                        {/* RESULTS GRID */}
                        <div className="results-content">
                            {loading ? (
                                <div className="loading-state">
                                    <div className="spinner"></div>
                                    <p>Searching solicitors...</p>
                                </div>
                            ) : hasSearched && filteredSolicitors.length === 0 ? (
                                <div className="no-results">
                                    <h3>No solicitors found</h3>
                                    <p>Try changing filters</p>
                                </div>
                            ) : (
                                <>
                                    <div className="results-header">
                                        <h3>Consult Best {cityQuery} {selectedCategory} Solicitor's Near You</h3>
                                    </div>

                                    <div className="solicitors-grid">
                                        {filteredSolicitors.map((s) => (
                                            <div key={s._id} className="solicitor-card">
                                                {/* Header */}
                                                <div className="solicitor-header">
                                                    {s.profile?.photo ? (
                                                        <img
                                                            src={s.profile.photo}
                                                            alt={s.name}
                                                            className="solicitor-image"
                                                        />
                                                    ) : (
                                                        <div className="solicitor-avatar">
                                                            {s.name.charAt(0)}
                                                        </div>
                                                    )}

                                                    {(() => {
                                                        const badge = getExpertBadge(s);
                                                        if (!badge) return null;

                                                        return (
                                                            <div className={`expert-badge ${badge.type}`}>
                                                                {badge.type === "gold" && <FaCrown className="badge-icon" />}
                                                                {badge.type === "silver" && <FaMedal className="badge-icon" />}
                                                                {badge.type === "bronze" && <FaAward className="badge-icon" />}
                                                                {badge.label}
                                                            </div>

                                                        );
                                                    })()}

                                                    <div className="solicitor-info">
                                                        <h3 className="solicitor-name">{s.name}</h3>
                                                        <p className="solicitor-firm">
                                                            {s.practice?.practiceName || "Independent Solicitor"}
                                                        </p>
                                                        <span className="solicitor-location">
                                                            <FiMapPin style={{ marginRight: "2px" }} />
                                                            {s.profile?.address?.city}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Body */}
                                                <div className="solicitor-body">
                                                    <div className="meta-item">
                                                        <span className="meta-label">Experience</span>
                                                        <span className="meta-value">
                                                            {s.profile?.experience || "N/A"}
                                                        </span>
                                                    </div>

                                                    <div className="meta-item">
                                                        <span className="meta-label">Level</span>
                                                        <span className="meta-value">
                                                            {s.profile?.advocateLevel || "N/A"}
                                                        </span>
                                                    </div>

                                                    <div className="meta-item">
                                                        <span className="meta-label">Gender</span>
                                                        <span className="meta-value">
                                                            {s.profile?.gender || "N/A"}
                                                        </span>
                                                    </div>

                                                    <div className="meta-item">
                                                        <span className="meta-label">Ratings</span>
                                                        <span className="meta-value">
                                                            {ratingsMap[s._id] && ratingsMap[s._id] > 0
                                                                ? renderStars(ratingsMap[s._id])
                                                                : "N/A"}
                                                        </span>
                                                    </div>

                                                    {/* Practice Category */}
                                                    <div className="meta-item">
                                                        <span className="meta-label">Services</span>
                                                        <span className="meta-value">
                                                            {hasSearched
                                                                ? selectedCategory
                                                                : s.practiceAreas?.[0]?.category || "N/A"}
                                                        </span>
                                                    </div>

                                                    {/* Practice Area */}
                                                    <div className="meta-item">
                                                        <span className="meta-label">Area</span>
                                                        <span className="meta-value">
                                                            {hasSearched
                                                                ? selectedArea
                                                                : s.practiceAreas?.[0]?.areas?.[0] || "N/A"}
                                                        </span>
                                                    </div>


                                                </div>

                                                {/* Footer */}
                                                <div className="solicitor-footer-wrapper">

                                                    {/* Buttons Row */}
                                                    <div className="solicitor-footer">
                                                        <Link
                                                            to={`/solicitor/${s._id}`}
                                                            className="view-profile-btn"
                                                        >
                                                            <FiUser style={{ marginRight: "6px" }} />
                                                            View Profile
                                                        </Link>

                                                        <button
                                                            className="contact-btn"
                                                            onClick={() =>
                                                                navigate(`/book-appointment/${s._id}`, {
                                                                    state: {
                                                                        practiceCategory: selectedCategory,
                                                                        practiceArea: selectedArea,
                                                                        fromSearch: true
                                                                    }
                                                                })
                                                            }
                                                        >
                                                            <FiCalendar style={{ marginRight: "6px" }} />
                                                            Book
                                                        </button>
                                                    </div>

                                                    {/* AI Recommendation Section */}
                                                    {s.aiMatchScore !== undefined && (
                                                        <div className="ai-recommend-section">

                                                            {/* Recommended Badge */}
                                                            <div className="recommended-label-wrapper">
                                                                {Number(s.aiMatchScore) > 60 ? (
                                                                    <div className="bottom-recommended">
                                                                        <HiSparkles className="bottom-ai-icon" />
                                                                        Recommended
                                                                    </div>
                                                                ) : (
                                                                    <div className="recommended-placeholder"></div>
                                                                )}
                                                            </div>

                                                            {/* Match Bar */}
                                                            <div className="match-bar-wrapper">
                                                                <div className="match-bar-bg">
                                                                    <div
                                                                        className="match-bar-fill"
                                                                        style={{ width: `${s.aiMatchScore || 0}%` }}
                                                                    ></div>
                                                                </div>

                                                                <span className="match-percentage">
                                                                    <BsBarChartFill className="bottom-ai-icon" />
                                                                    {s.aiMatchScore || 0}%
                                                                </span>
                                                            </div>

                                                        </div>
                                                    )}

                                                </div>

                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </>
    );
}